import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ErpFileImportPreview, ErpFileImportState } from './erp-file-import.types';

const FIRST = 0;
const ONE = 1;

/**
 * The workspace's "ERP" button for a closing fed from a FILE — a pure view (REV-22007, ADR-0020).
 *
 * A file closing has no connection to pull from, so the button that refreshes a vendor pull opens
 * this instead: pick the updated trial balance, see what the server read from it, confirm the import.
 * Three frames of one flow, one dialog:
 *
 *   1. **pick** — nothing uploaded yet (`idle`), or the last file was refused (`refused`, with its
 *      rows, so the trustee fixes the file once instead of once per error);
 *   2. **check** — the server is parsing (`uploading`);
 *   3. **confirm** — the file is stored and previewed (`ready`); "Importieren" starts the import,
 *      which the container runs through the SAME progress modal and summary as a vendor pull.
 *
 * It holds no vocabulary of its own beyond its i18n keys: the preview and the refusal lines arrive
 * formatted. It does no HTTP and knows nothing of the scan — the import's 409-and-retry lives in the
 * effect behind the container.
 *
 * Hand-rolled rather than `ham-modal` for the reason the setup wizard gives: `ham-modal` moves itself
 * into `#global-modal-placeholder`, which exists only in the app shell, so the Component Lab could not
 * render this view. `overlay` switches the backdrop off for exactly that.
 */
@Component({
    selector: 'abcp-erp-file-import',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './erp-file-import.component.html',
    styleUrls: ['./erp-file-import.component.scss'],
})
export class ErpFileImportComponent {
    @Input() state: ErpFileImportState = 'idle';

    /** The stored file's preview. Only rendered in `ready`. */
    @Input() preview: ErpFileImportPreview | null = null;

    /** Why the file was refused, one translated line each — the first one is the lead. */
    @Input() refusal: readonly string[] = [];

    /** True from "Importieren" until the container closes the dialog. Disables every control. */
    @Input() importing = false;

    /**
     * Why the last IMPORT failed (not the upload) — translated by the container. The dialog reopens on
     * it with the file still previewed, so "Importieren" can simply be pressed again.
     */
    @Input() importError: string | null = null;

    /** The period the import will write, e.g. "01.01.2026 – 31.12.2026". Null hides the line. */
    @Input() periodLabel: string | null = null;

    /** Draw the fixed backdrop. Off in the Component Lab, where the view renders inline. */
    @Input() overlay = true;

    @Output() fileSelected = new EventEmitter<File>();
    @Output() templateRequested = new EventEmitter<void>();
    @Output() confirmed = new EventEmitter<void>();
    @Output() closed = new EventEmitter<void>();

    get isUploading(): boolean {
        return this.state === 'uploading';
    }

    get isReady(): boolean {
        return this.state === 'ready' && this.preview !== null;
    }

    get isRefused(): boolean {
        return this.state === 'refused';
    }

    /** Import is offered only for a previewed file, and only once. */
    get canConfirm(): boolean {
        return this.isReady && !this.importing;
    }

    get refusalLead(): string | null {
        return this.refusal[FIRST] ?? null;
    }

    get refusalRows(): readonly string[] {
        return this.refusal.slice(ONE);
    }

    /** Forwards the picked file, then clears the input so re-picking the SAME fixed file fires again. */
    public onFilePicked(event: Event): void {
        const input = event.target as HTMLInputElement | null;
        const file = input?.files?.item(FIRST) ?? null;
        if (file !== null) {
            this.fileSelected.emit(file);
        }
        if (input !== null) {
            input.value = '';
        }
    }

    public onTemplate(): void {
        this.templateRequested.emit();
    }

    public onConfirm(): void {
        if (this.canConfirm) {
            this.confirmed.emit();
        }
    }

    /** A running import cannot be dismissed — there is no cancel on the route (same rule as the sync). */
    public onClose(): void {
        if (!this.importing) {
            this.closed.emit();
        }
    }

    public trackByLine(index: number): number {
        return index;
    }
}
